/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ChangeEvent, FocusEvent } from 'react';
import type { TFunction } from 'i18next';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000Label from '../../../u2000Core/components/formControls/label/U2000Label';
import { NS } from '../../U3208';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { u3208IsSystemCorpus, type U3208AiRagCorpusFormRow } from '../../U3208Service';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { U3208ROLES } from '../../U3208';
import { u2000CanManageScopeAwareEntity } from '../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3208AiRagCorpusHeaderProps {
  formStateHandlers: U2000FormStateHandlers<U3208AiRagCorpusFormRow>;
  t: TFunction;
}

function U3208AiRagCorpusHeader({ formStateHandlers, t }: U3208AiRagCorpusHeaderProps) {
  const { auth, hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const isSystem = data ? u3208IsSystemCorpus(data) : false;
  const formEditMode = formStateHandlers.getFormEditMode();
  const canEditCorpus = u2000CanManageScopeAwareEntity({
    isSystemScope: isSystem,
    hasRequiredRole: hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3208ROLES.ADD : U3208ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || !canEditCorpus;
  const isCreate = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const errors = formStateHandlers.getErrors();
  const touched = formStateHandlers.getTouched();
  const submitted = formStateHandlers.isSubmitted();

  const activeChecked = data?.active === true;
  const errActive = errors.active;
  const hasActiveError = (touched.active && errActive !== undefined) || (submitted && errActive !== undefined);

  const setActiveFromCheckbox = (checked: boolean) => {
    const ev = { target: { id: 'active' } } as ChangeEvent<HTMLInputElement>;
    formStateHandlers.onChange?.(ev, checked);
  };

  const blurActive = () => {
    formStateHandlers.onBlur?.({ target: { id: 'active' } } as FocusEvent<HTMLInputElement>);
  };

  if (!data) return null;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {isSystem ? (
        <div className="md:col-span-2">
          <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {t('badge.system', { ns: NS })}
          </span>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col gap-1.5">
        <U2000Label htmlFor="code" label={t('lblCode', { ns: NS })} required={isCreate} />
        <U2000InputText
          hideLabel
          formStateHandlers={formStateHandlers as any}
          fieldName="code"
          label={t('lblCode', { ns: NS })}
          required={isCreate}
          readonly={readonly || !isCreate}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <U2000Label htmlFor="title" label={t('lblTitle', { ns: NS })} required />
        <U2000InputText
          hideLabel
          formStateHandlers={formStateHandlers as any}
          fieldName="title"
          label={t('lblTitle', { ns: NS })}
          required
          readonly={readonly}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <U2000Label htmlFor="active" label={t('lblActive', { ns: NS })} required />
        <div className="flex items-center pt-1">
          <input
            id="active"
            type="checkbox"
            checked={activeChecked}
            disabled={readonly}
            onChange={(e) => setActiveFromCheckbox(e.target.checked)}
            onBlur={blurActive}
            className="h-4 w-4 rounded-sm border-gray-300"
            aria-invalid={hasActiveError}
          />
        </div>
      </div>
    </div>
  );
}

export default U3208AiRagCorpusHeader;
